import { toaster, Message } from "rsuite";
import { Buffer } from "buffer";
import { WebSocketSetup } from "../../views/socket";
import { checkAcl } from "../../adminressources/ACLHelper.js";
import {
  getToken,
  decrypt_storage_values,
  encrypt_storage_values,
  isJWT,
  validateTokenIntegrity,
  isImpersonationSession,
  storeImpersonationLogin,
  clearImpersonationLogin,
} from "./helper";
import { config as getConfig } from "./Config.js";
import Cookies from "universal-cookie";

export var MultiCampus = null;
const MISSING_TOKEN_SENTINEL = "NO TOKEN FOUND IN HEADER OR BODY";

const SSO_SESSION_VALIDATION_PENDING_KEY = "SB_SSO_SESSION_VALIDATION_PENDING";
export const SSO_SESSION_VALIDATION_FAILED_KEY = "SB_SSO_SESSION_VALIDATION_FAILED";

function isSsoCallbackToken() {
  return window.location.hash.startsWith("#token:");
}

function markSsoSessionValidationPending() {
  sessionStorage.setItem(SSO_SESSION_VALIDATION_PENDING_KEY, "1");
}

function clearSsoSessionValidationPending() {
  sessionStorage.removeItem(SSO_SESSION_VALIDATION_PENDING_KEY);
}

function isPendingSsoSessionValidation(token) {
  return (
    token !== MISSING_TOKEN_SENTINEL &&
    isSsoCallbackToken() &&
    sessionStorage.getItem(SSO_SESSION_VALIDATION_PENDING_KEY) === "1"
  );
}

// A backend restart intentionally drops its in-memory session list.  The browser
// must not keep sending the now-unrecognised token indefinitely: a subsequent
// request without it can begin the normal SSO flow again.
export function clear_login_session() {
  if (isImpersonationSession()) {
    clearImpersonationLogin();
    return;
  }
  const cookies = new Cookies();
  localStorage.removeItem("L");
  localStorage.removeItem("T");
  localStorage.removeItem("D");
  localStorage.removeItem("valid");
  cookies.remove("T", { path: "/" });
  cookies.remove("E", { path: "/" });

  if (isSsoCallbackToken()) {
    window.history.replaceState(
      null,
      "Login",
      `${window.location.pathname}${window.location.search}`,
    );
  }
}

// Helper function to retrieve token from multiple sources
function retrieveToken() {
  let token = getToken();

  // Check token in URL fragments
  if (document.URL.match("#token")) {
    token = document.URL.split("#token:")[1];
    // Validate integrity of SSO token from URL fragment (#4)
    if (token && isJWT(token)) {
      const integrity = validateTokenIntegrity(token);
      if (!integrity.valid) {
        console.warn(`[Auth] SSO token from URL failed integrity check: ${integrity.reason}`);
        token = null;
      }
    }
  } else if (document.URL.match("samllogin/#token")) {
    token = document.URL.split("#token:")[1];
    // Validate integrity of SAML token from URL fragment (#4)
    if (token && isJWT(token)) {
      const integrity = validateTokenIntegrity(token);
      if (!integrity.valid) {
        console.warn(`[Auth] SAML token from URL failed integrity check: ${integrity.reason}`);
        token = null;
      }
    }
  }

    return token || MISSING_TOKEN_SENTINEL;
}

// Helper function to create headers
function createHeaders(token) {
    const headers = {
        'Content-Type': 'application/json',
        'Source': 'media'
    };

    if (token && token !== MISSING_TOKEN_SENTINEL) {
        headers['Semabench-Token'] = token;
    }

    return headers;
}

// Method for when a user submits a password
export async function submit_login(username, pass) {
  if (!pass) {
    toaster.clear();
    toaster.push(
      <Message type="error" showIcon closable>
        Please enter a valid password
      </Message>,
      { duration: 5000 },
    );
    return null;
  }

  // Encrypts the pass to base64
  var password = Buffer.from(pass).toString("base64");

  try {
    const response = await fetch("../api/v1/repo/s/semabench/ticket", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Source: "media",
      },
      body: JSON.stringify({
        name: username,
        value: password,
        source: "media",
      }),
    });

    if (response.status === 401) {
      toaster.clear();
      toaster.push(
        <Message type="error">
          You do not have View permissions, please contact your IT support
        </Message>,
        { duration: 5000 },
      );
      return null;
    }

    return response.json();
  } catch (error) {
    return null;
  }
}

// Checks if there is a valid token or logs in on first start
export async function check_login({ retryWithoutToken = false } = {}) {
  const token = retryWithoutToken ? MISSING_TOKEN_SENTINEL : retrieveToken();
  const headers = createHeaders(token);

  try {
    const pendingSsoSessionValidation = isPendingSsoSessionValidation(token);
    const response = await fetch("../api/v1/repo/s/semabench/ticket", {
      method: "GET",
      headers,
    });

    const url = response.headers.get("location");
    if (url && token === MISSING_TOKEN_SENTINEL) {
      markSsoSessionValidationPending();
      window.location.replace(url);
      return null;
    }

    // The IdP has returned a new SchoolBench token, but it cannot be used.
    // Stop here rather than treating that new token as another stale session
    // and beginning SSO again.
    if (response.status === 401 && pendingSsoSessionValidation) {
      clear_login_session();
      clearSsoSessionValidationPending();
      return { sso_session_validation_failed: true };
    }

    // A valid browser token can be unknown after the backend deliberately
    // clears its in-memory sessions.  Clear it and make one no-token request,
    // which is the backend's normal SSO initiation path.  Do not retry a
    // no-token request, so an actual IdP/server problem cannot loop.
    if (response.status === 401 && token !== MISSING_TOKEN_SENTINEL && !retryWithoutToken) {
      clear_login_session();
      if (isImpersonationSession()) return null;
      return check_login({ retryWithoutToken: true });
    }

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    if (data?.ticket) {
      clearSsoSessionValidationPending();
      sessionStorage.removeItem(SSO_SESSION_VALIDATION_FAILED_KEY);
    }
    return data;
    } catch (error) {
        return null;
    }
}

// Checks if login token is valid with boolean return
export async function check_login_bool() {
  const token = retrieveToken();
  const headers = createHeaders(token);

  try {
    const pendingSsoSessionValidation = isPendingSsoSessionValidation(token);
    const response = await fetch("../api/v1/repo/s/semabench/ticket", {
      method: "GET",
      headers,
    });

        const url = response.headers.get("location");
        if (url && token === MISSING_TOKEN_SENTINEL) {
            markSsoSessionValidationPending();
            window.location.replace(url);
            return false;
        }

    if (response.status === 401) {
      if (pendingSsoSessionValidation) {
        clear_login_session();
        clearSsoSessionValidationPending();
        sessionStorage.setItem(SSO_SESSION_VALIDATION_FAILED_KEY, "1");
      }
      return false;
    }

    const data = await response.json();

    if (data?.ticket) {
      clearSsoSessionValidationPending();
      sessionStorage.removeItem(SSO_SESSION_VALIDATION_FAILED_KEY);
      multicampus_checker(data);
      storeLoginSession(data); // Store session data if a valid ticket is present
      WebSocketSetup();
      return true;
    } else {
      return false; // No ticket found
    }
  } catch (error) {
    return false;
  }
}

// Store session data after login (supports both JWT and AES token formats)
function storeLoginSession(data) {

  if (isImpersonationSession()) {
    storeImpersonationLogin(data);
    return;
  }

    const cookies = new Cookies();
    localStorage.setItem("valid", 'Okay');

  // Detect token type for session diagnostics
  const ticket = data.ticket || "";
  const tokenType = ticket.split(".").length === 3 ? "JWT" : "AES";
  console.debug(
    `[Auth] Storing ${tokenType} session for user: ${data.username}`,
  );

  let expires = cookies.get("E");
  expires = expires
    ? JSON.parse(decrypt_storage_values(expires))
    : new Date(Date.now() + 1000 * 60 * 60 * 12); // Set default expiration

  const encryptedExpires = encrypt_storage_values(
    JSON.stringify(new Date(expires)),
  );
  localStorage.setItem("L", encrypt_storage_values(JSON.stringify(data)));
  localStorage.setItem("T", encrypt_storage_values(data.ticket));
  cookies.set("T", encrypt_storage_values(data.ticket), {
    path: "/",
    expires: new Date(expires),
    secure: true,
    sameSite: "Strict",
  });
  cookies.set("E", encryptedExpires, {
    path: "/",
    expires: new Date(expires),
    secure: true,
    sameSite: "Strict",
  });
}

// Checks for multi-campus setup on login
export function multicampus_checker(login) {
  let config = getConfig;
  const login_detail = login;

  // If the user is an admin, skip the process and return 'admin'
  if (checkAcl(login_detail.roles, config.acls, "ADMIN")) {
    return "admin";
  }

  if (config.multicampus) {
    const multicampus = config.multicampus;
    const multicampusLength = multicampus.length;

    if (multicampusLength > 0) {
      let campus = [];

      // Loop through the multicampus array and check roles
      for (let x = 0; x < multicampusLength; x++) {
        const roleFound = multicampus[x].roles.some((role) =>
          login_detail.roles.includes(role),
        );

        if (roleFound) {
          campus = campus.concat(multicampus[x].campus); // Add campus to the array
        }
      }

      // Remove duplicates
      campus = [...new Set(campus)];

      MultiCampus = campus;
      return campus;
    }
  }

  return null;
}
